import {
  type DocumentViewerKind,
  documentViewerKind,
} from "@volca/ui/components/ui/document-viewer";
import type { ProfitabilityDocument } from "@/profitability/types";

/** The ways to look at a document, in the order the pane offers them. */
export const VIEWS = [
  { value: "original", label: "Original" },
  { value: "preview", label: "Preview" },
  { value: "used", label: "Used for" },
  { value: "details", label: "Details" },
] as const;

export type DocumentView = (typeof VIEWS)[number]["value"];

/**
 * The viewer that draws the file as uploaded (a CSV is drawn as a
 * spreadsheet); null when none does (text, TIFF, slides).
 */
export const originalKind = (
  document: Pick<ProfitabilityDocument, "content_type" | "file_name">,
): DocumentViewerKind | null =>
  documentViewerKind({
    contentType: document.content_type,
    fileName: document.file_name,
  });

export const isSpreadsheet = (kind: DocumentViewerKind | null) =>
  kind === "xlsx" || kind === "csv";

/**
 * The views a document offers: Original when a viewer draws the file, and
 * Preview (the tables Volca read) unless the file is a spreadsheet, whose
 * Original already is its rows: for a PDF, an image or a Word file the
 * Preview shows the tables read off its pages; for text, the text.
 */
export const documentViews = (kind: DocumentViewerKind | null) =>
  VIEWS.filter(
    (view) =>
      (view.value !== "original" || kind !== null) &&
      (view.value !== "preview" || !isSpreadsheet(kind)),
  );

/**
 * The view to show: the one asked for (`?view=`) when the document offers
 * it, else Original where there is one, else Preview.
 */
export const documentView = (
  requested: string | null,
  kind: DocumentViewerKind | null,
): DocumentView => {
  const offered = documentViews(kind);
  return (
    offered.find((view) => view.value === requested)?.value ?? offered[0].value
  );
};
